import React, { createContext, useContext, useState, ReactNode } from 'react';

// Mock user type matching the Supabase user structure
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'user' | 'admin' | 'superadmin';
  roles?: string[];
  orgId?: string;
  plan?: string;
  isSuperAdmin?: boolean;
  companyId?: string;
}

interface MockAuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  loginWithRedirect: (options?: Record<string, unknown>) => void;
  logout: () => void;
  getAccessToken: () => Promise<string>;
  refreshUserProfile: () => Promise<void>;
  error: string | null;
}

const MockAuthContext = createContext<MockAuthContextType | undefined>(undefined);

// Mock users database with valid UUIDs for database compatibility
// Using the Teal Packaging company ID where real Stripe invoices are synced
const MOCK_COMPANY_UUID = '8f637fcf-66db-41be-b4b6-b409b64054df'; // Teal Packaging organization

const MOCK_USERS = {
  'admin@test.com': {
    id: 'mock-admin-123',
    email: 'admin@test.com',
    name: 'Admin User',
    role: 'admin' as const,
    roles: ['admin'],
    orgId: MOCK_COMPANY_UUID,
    companyId: MOCK_COMPANY_UUID,
    plan: 'growth',
    isSuperAdmin: false
  },
  'buttsahal0@gmail.com': {
    id: 'ea118cd7-1718-48e3-ab13-5ff1c65bc748',
    email: 'buttsahal0@gmail.com',
    name: 'Sahal Sajid (Teal Packaging)',
    role: 'admin' as const,
    roles: ['admin'],
    orgId: MOCK_COMPANY_UUID,
    companyId: MOCK_COMPANY_UUID,
    plan: 'growth',
    isSuperAdmin: false
  },
  'user@test.com': {
    id: 'mock-user-456',
    email: 'user@test.com',
    name: 'Regular User',
    role: 'user' as const,
    roles: ['user'],
    orgId: MOCK_COMPANY_UUID,
    companyId: MOCK_COMPANY_UUID,
    plan: 'starter',
    isSuperAdmin: false
  },
  'superadmin@test.com': {
    id: 'mock-superadmin-789',
    email: 'superadmin@test.com',
    name: 'Super Admin',
    role: 'superadmin' as const,
    roles: ['superadmin'],
    orgId: MOCK_COMPANY_UUID,
    companyId: MOCK_COMPANY_UUID,
    plan: 'enterprise',
    isSuperAdmin: true
  }
};

export const MockAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    // Check if user was previously logged in
    const savedUser = localStorage.getItem('mock_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [isLoading, setIsLoading] = useState(false);

  const login = () => {
    // This is handled by the login page
    window.location.href = '/login';
  };

  const loginWithRedirect = (options?: Record<string, unknown>) => {
    // For mock auth, just go to login page
    window.location.href = '/login';
  };

  const loginWithCredentials = (email: string, password: string) => {
    setIsLoading(true);
    
    // Simulate network delay
    setTimeout(() => {
      const mockUser = MOCK_USERS[email as keyof typeof MOCK_USERS];
      
      if (mockUser && password === 'password123') {
        setUser(mockUser);
        localStorage.setItem('mock_user', JSON.stringify(mockUser));
        localStorage.setItem('mock_token', 'mock-jwt-token-' + Date.now());
        setIsLoading(false);
        
        // Redirect to dashboard
        window.location.href =  '/dashboard';
      } else {
        setIsLoading(false);
        alert('Invalid credentials. Use admin@test.com, buttsahal0@gmail.com, or user@test.com / password123');
      }
    }, 500);
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('mock_user');
    localStorage.removeItem('mock_token');
    window.location.href = '/';
  };

  const getAccessToken = async () => {
    return localStorage.getItem('mock_token') || 'mock-jwt-token';
  };

  const refreshUserProfile = async () => {
    // Mock refresh - just return current user
    return Promise.resolve();
  };

  const value: MockAuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    loginWithRedirect,
    logout,
    getAccessToken,
    refreshUserProfile,
    error: null
  };

  // Expose loginWithCredentials for the login page
  (window as Record<string, unknown>).mockLogin = loginWithCredentials;

  // Expose context for unified auth system
  (window as Record<string, unknown>).__MockAuthContext = MockAuthContext;

  return (
    <MockAuthContext.Provider value={value}>
      {children}
    </MockAuthContext.Provider>
  );
};

export const useMockAuth = () => {
  const context = useContext(MockAuthContext);
  if (context === undefined) {
    throw new Error('useMockAuth must be used within a MockAuthProvider');
  }
  return context;
};
